import { ITicketInsightCategoryBreakdown } from "../../../models/Tickets/TicketInsight";
import { formatHours } from "../TicketAvgResponseTable";
import { BarRow, Card, EmptyState, StackedBar } from "./InsightWidgets";
import {
  categoryBarClassMap,
  categoryLabels,
  categoryOrder,
  CategoryPill,
} from "./insightMeta";

interface CategoryBreakdownCardProps {
  categories: ITicketInsightCategoryBreakdown[];
}

/**
 * How the queue splits by what the model thinks each ticket actually is. Bars
 * are the share of all analysed tickets; the table underneath carries the
 * timings, which is where "features take three times longer than bugs" shows up.
 */
export default function CategoryBreakdownCard({
  categories,
}: CategoryBreakdownCardProps) {
  const ordered = categoryOrder
    .map((c) => categories.find((x) => x.category === c))
    .filter((x): x is ITicketInsightCategoryBreakdown => !!x);

  const total = ordered.reduce((sum, c) => sum + c.count, 0);
  const max = Math.max(0, ...ordered.map((c) => c.count));

  return (
    <Card
      title="Bugs vs features vs everything else"
      description="Where the model puts each ticket, regardless of the Bug/Feature type set by hand."
    >
      {total === 0 ? (
        <EmptyState>No analysed tickets in this scope yet.</EmptyState>
      ) : (
        <div className="space-y-4">
          <StackedBar
            className="h-4"
            segments={ordered.map((c) => ({
              key: categoryLabels[c.category],
              value: c.count,
              className: categoryBarClassMap[c.category],
              title: `${categoryLabels[c.category]}: ${c.count} (${c.percent}%)`,
            }))}
          />

          <div className="space-y-1.5">
            {ordered
              .filter((c) => c.count > 0)
              .map((c) => (
                <BarRow
                  key={c.category}
                  label={<CategoryPill category={c.category} />}
                  value={c.count}
                  max={max}
                  barClassName={categoryBarClassMap[c.category]}
                  valueLabel={
                    <span>
                      {c.count}{" "}
                      <span className="text-xs text-gray-500">
                        ({c.percent}%)
                      </span>
                    </span>
                  }
                />
              ))}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500">
                  <th className="py-1 pr-3">Category</th>
                  <th className="py-1 pr-3 text-right">Active</th>
                  <th className="py-1 pr-3 text-right">Done</th>
                  <th className="py-1 pr-3 text-right">Avg first reply</th>
                  <th className="py-1 text-right">Avg resolution</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ordered
                  .filter((c) => c.count > 0)
                  .map((c) => (
                    <tr key={c.category}>
                      <td className="py-1.5 pr-3 text-slate-800">
                        {categoryLabels[c.category]}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {c.active}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {c.done}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {formatHours(c.avgFirstReplyHours)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {formatHours(c.avgResolutionHours)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
